import React, { useEffect, useMemo, useState } from 'react';
import { FiBell, FiLock, FiUser } from 'react-icons/fi';
import { changePassword, getMe } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import {
  disableBrowserPushNotifications,
  enableBrowserPushNotifications,
  loadBrowserPushState,
} from '../utils/webPushNotifications';

const MyProfilePage = () => {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pushState, setPushState] = useState({
    loading: true,
    saving: false,
    supported: true,
    configured: false,
    permission: 'default',
    serverSubscription: null,
    error: '',
  });
  const { user } = useCurrentUser();
  const canUpdateProfile = hasPermission(user, 'profile.update');
  const displayName = name || 'Usuario';
  const initials = useMemo(() => (
    displayName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  ), [displayName]);

  useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      try {
        const profile = await getMe();
        if (!mounted) return;
        setName(profile?.name || '');
        setUsername(profile?.username || '');
      } catch (requestError) {
        console.log(requestError);
        if (mounted) setError('Error al cargar el perfil.');
      }
    };
    fetchProfile();
    return () => {
      mounted = false;
    };
  }, []);

  const refreshPushState = async () => {
    setPushState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const state = await loadBrowserPushState();
      setPushState({
        loading: false,
        saving: false,
        supported: state.supported,
        configured: state.configured,
        permission: state.permission,
        serverSubscription: state.serverSubscription,
        error: '',
      });
    } catch (requestError) {
      setPushState((prev) => ({
        ...prev,
        loading: false,
        saving: false,
        error: requestError?.message || 'No se pudo cargar el estado de notificaciones.',
      }));
    }
  };

  useEffect(() => {
    refreshPushState();
  }, []);

  const handleEnablePush = async () => {
    setPushState((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      await enableBrowserPushNotifications();
      await refreshPushState();
      setMessage('Notificaciones del navegador habilitadas.');
    } catch (requestError) {
      setPushState((prev) => ({
        ...prev,
        saving: false,
        error: requestError?.message || 'No se pudieron habilitar las notificaciones.',
      }));
    }
  };

  const handleDisablePush = async () => {
    setPushState((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      await disableBrowserPushNotifications();
      await refreshPushState();
      setMessage('Notificaciones de este navegador deshabilitadas.');
    } catch (requestError) {
      setPushState((prev) => ({
        ...prev,
        saving: false,
        error: requestError?.message || 'No se pudieron deshabilitar las notificaciones.',
      }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!canUpdateProfile) return;

    if (newPassword !== confirmPassword) {
      setError('Las contrasenas no coinciden.');
      return;
    }

    if (newPassword.length < 8) {
      setError('La nueva contrasena debe tener al menos 8 caracteres.');
      return;
    }

    try {
      setLoading(true);
      await changePassword({ password: currentPassword, newPassword });
      setMessage('La contrasena ha sido actualizada exitosamente.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'Error al cambiar la contrasena.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-24 px-4 pb-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow p-6 border border-blue-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-lg font-semibold">
                {initials}
              </div>
              <div>
                <h1 className="text-2xl font-bold">Mi Perfil</h1>
                <p className="text-sm text-gray-600">
                  Administra tus datos y seguridad.
                </p>
              </div>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full ${
              canUpdateProfile ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {canUpdateProfile ? 'Puedes actualizar tu contrasena' : 'Sin permisos de edicion'}
            </span>
          </div>
        </div>

        {(message || error) && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${
            message
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {message || error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
          <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4 text-gray-700">
              <FiUser />
              <h2 className="text-lg font-semibold">Datos personales</h2>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nombre</label>
                <input
                  type="text"
                  value={name}
                  disabled
                  className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nombre de Usuario</label>
                <input
                  type="text"
                  value={username}
                  disabled
                  className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-700"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4 text-gray-700">
              <FiLock />
              <h2 className="text-lg font-semibold">Seguridad</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              La nueva contrasena debe tener al menos 8 caracteres.
            </p>
            <form onSubmit={handlePasswordChange}>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Contrasena Actual</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                  disabled={!canUpdateProfile}
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Nueva Contrasena</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                  disabled={!canUpdateProfile}
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Confirmar Nueva Contrasena</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                  disabled={!canUpdateProfile}
                />
              </div>
              <div className="flex justify-end">
                {canUpdateProfile && (
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-300"
                    disabled={loading}
                  >
                    {loading ? 'Actualizando...' : 'Cambiar Contrasena'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-700">
                <FiBell />
                <h2 className="text-lg font-semibold">Notificaciones del navegador</h2>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Recibe avisos de nuevas ordenes de compra en este navegador.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className={`rounded-full px-2 py-1 ${
                  pushState.supported ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                }`}>
                  {pushState.supported ? 'Navegador compatible' : 'No compatible'}
                </span>
                <span className={`rounded-full px-2 py-1 ${
                  pushState.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {pushState.configured ? 'Servidor configurado' : 'Servidor sin claves push'}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
                  Permiso: {pushState.permission}
                </span>
                <span className={`rounded-full px-2 py-1 ${
                  pushState.serverSubscription ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {pushState.serverSubscription ? 'Este navegador esta suscrito' : 'Este navegador no esta suscrito'}
                </span>
              </div>
              {pushState.error && (
                <p className="mt-3 text-sm text-red-600">{pushState.error}</p>
              )}
              {!pushState.loading && !pushState.supported && (
                <p className="mt-3 text-sm text-red-600">
                  Este navegador no soporta notificaciones push.
                </p>
              )}
              {!pushState.loading && pushState.supported && !pushState.configured && (
                <p className="mt-3 text-sm text-amber-700">
                  Faltan las claves push del servidor para habilitar esta funcion.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={refreshPushState}
                disabled={pushState.loading || pushState.saving}
                className="rounded border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-60"
              >
                {pushState.loading ? 'Cargando...' : 'Actualizar'}
              </button>
              {pushState.serverSubscription ? (
                <button
                  type="button"
                  onClick={handleDisablePush}
                  disabled={pushState.loading || pushState.saving}
                  className="rounded bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-60"
                >
                  {pushState.saving ? 'Guardando...' : 'Deshabilitar este navegador'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleEnablePush}
                  disabled={!pushState.supported || !pushState.configured || pushState.loading || pushState.saving}
                  className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {pushState.saving ? 'Guardando...' : 'Habilitar notificaciones'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfilePage;
